
import { createVNode, defineComponent } from 'vue';
import { mapState } from 'vuex';
import { message, Modal } from 'ant-design-vue';
import {
  CheckOutlined,
  EditOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons-vue';
import numeral from 'numeral';
import cloneDeep from 'lodash.clonedeep';
import QuantityInput from '@/components/quantity-input/QuantityInput.vue';
import {
  getShoppingcartPage,
  deleteShoppingcart,
  modifyShoppingcart,
  submitOrder,
  deleteShoppingcartItemById
} from '@/api/apis';
import { baseFileUrl } from '@/utils/constants';
import DeleteIcon from '@/components/icons/DeleteIcon.vue';
import PreviewImageModal from '@/components/preview-image/PreviewImageModal.vue';

interface VehicleCartProps {
  dataSource: any[];
  originDataSource: any[];
  selectedRowKeys: any[];
  previewImg: any[];
  [key: string]: any;
}

export default defineComponent({
  name: 'VehicleCart',
  components: {
    QuantityInput,
    DeleteIcon,
    PreviewImageModal,
    CheckOutlined,
    EditOutlined
  },
  props: {
    consigneeDropdownList: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data(): VehicleCartProps {
    return {
      previewImg: [],
      showAttributeImgModal: false,
      numeral,
      baseFileUrl,
      queryParam: {
        consignee: '',
        modelNum: ''
      },
      selectedRowKeys: [],
      loading: false,
      isSubmitting: false,
      colTitle: [
        'photo',
        'model',
        'color',
        'unit',
        'quantity',
        'unit_price',
        'total_price',
        'action',
        'consignee',
        'remark'
      ],
      columns: [
        {
          dataIndex: 'img',
          width: 100,
          slots: { customRender: 'photo', title: 'photo-title' }
        },
        {
          dataIndex: 'model',
          width: 120,
          slots: { customRender: 'model', title: 'model-title' }
        },
        {
          dataIndex: 'color',
          width: 100,
          slots: { customRender: 'color', title: 'color-title' }
        },
        {
          dataIndex: 'unit',
          width: 100,
          slots: { customRender: 'unit', title: 'unit-title' }
        },
        {
          dataIndex: 'quantity',
          width: 140,
          slots: { customRender: 'quantity', title: 'quantity-title' }
        },
        {
          dataIndex: 'unitPrice',
          width: 100,
          slots: { customRender: 'unitPrice', title: 'unit_price-title' }
        },
        {
          dataIndex: 'totalPrice',
          width: 100,
          slots: { customRender: 'totalPrice', title: 'total_price-title' }
        },
        {
          dataIndex: 'consignee',
          width: 140,
          slots: { title: 'consignee-title', customRender: 'consignee' }
        },
        {
          dataIndex: 'remark',
          width: 120,
          slots: { title: 'remark-title', customRender: 'remark' }
        },
        {
          width: 100,
          slots: { title: 'action-title', customRender: 'action' }
        }
      ],
      dataSource: [],
      originDataSource: []
    };
  },
  created() {
    this.loadData();
  },
  computed: {
    ...mapState(['userInfo']),
    totalSummary() {
      const items = this.dataSource.filter(s =>
        this.selectedRowKeys.includes(s.id)
      );

      let total = numeral(0);

      items.forEach(s => {
        total = total.add(s.totalPrice || 0);
      });

      const value = total.value();

      return value ? total.format('$0,0.00') : '';
    }
  },
  methods: {
    handleClear() {
      Modal.confirm({
        // title: 'Are you sure delete this task?',
        icon: createVNode(ExclamationCircleOutlined),
        content: this.$t('shopping_cart.delete_confirm'),
        okText: this.$t('shopping_cart.confirm'),
        cancelText: this.$t('shopping_cart.cancel'),
        onOk: async () => {
          const res = await deleteShoppingcartItemById({
            ids: this.selectedRowKeys.join(',')
          });

          if (res.success) {
            this.selectedRowKeys = [];
            this.loadData();
          }
        }
      });
    },
    async handleSubmitOrder() {
      const items = this.dataSource
        .filter(s => this.selectedRowKeys.includes(s.id))
        .map(s => {
          return {
            id: s.id,
            quantity: s.quantity,
            remark: s.remark
          };
        });

      this.isSubmitting = true;
      const res = await submitOrder(items);

      if (res.success) {
        this.selectedRowKeys = [];
        this.loadData();
      } else {
        message.error(this.$t(res.message));
      }
      this.isSubmitting = false;
    },
    getPhoto(record) {
      const imgs = (record.img || '').split(',').filter(s => s);

      return imgs.length > 0 ? baseFileUrl + imgs[0] : '';
    },
    async handleSave(record) {
      const res = await modifyShoppingcart({
        ...record
      });

      if (res.success) {
        record.editable = false;
        record.conEditable = false;
        const oIndex = this.originDataSource.findIndex(s => s.id === record.id);
        if (oIndex > -1) {
          this.originDataSource.splice(oIndex, 1, { ...record });
        }
      }
    },
    handleEdit(record) {
      record.editable = true;
    },
    handleConsigneeEdit(record) {
      record.conEditable = true;
    },
    handleShowAttrImg(record) {
      this.previewImg.push(
        record.attributeImg ? `${baseFileUrl}${record.attributeImg}` : ''
      );
      this.showAttributeImgModal = true;
    },
    handleAfterImgModalClose() {
      this.previewImg = [];
      this.showAttributeImgModal = false;
    },
    async loadData() {
      this.loading = true;
      const res = await getShoppingcartPage({
        modelNum: this.queryParam.modelNum,
        consignee: this.queryParam.consignee,
        shoppingType: '1',
        pageNo: 1,
        pageSize: 2147483647
      });

      if (res.success) {
        this.dataSource = res.result.records || [];

        this.originDataSource = cloneDeep(this.dataSource);
      }

      this.loading = false;
    },
    onSelectChange(selectedRowKeys) {
      this.selectedRowKeys = selectedRowKeys;
    },
    async handleQuantityChange(record, quantity) {
      const res = await modifyShoppingcart({
        ...record,
        quantity
      });

      if (res.success) {
        record.totalPrice = record.unitPrice
          ? numeral(quantity)
              .multiply(record.unitPrice || 0)
              .value()
          : null;
      }

      record.quantity = quantity;
    },
    handleReset() {
      this.queryParam = {
        consignee: '',
        modelNum: ''
      };
      this.dataSource = this.originDataSource;
    },
    handleSearch() {
      this.dataSource = this.originDataSource.filter(
        s =>
          ((this.queryParam.modelNum &&
            s.modelNum
              ?.toUpperCase()
              .indexOf(this.queryParam.modelNum.toUpperCase()) > -1) ||
            !this.queryParam.modelNum) &&
          ((this.queryParam.consignee &&
            s.consignee
              ?.toUpperCase()
              .indexOf(this.queryParam.consignee.toUpperCase()) > -1) ||
            !this.queryParam.consignee)
      );
    },
    async handleDelete(record) {
      const res = await deleteShoppingcart({
        id: record.id
      });

      if (res.success) {
        this.dataSource = this.dataSource.filter(
          (s: any) => s.id !== record.id
        );

        this.originDataSource = cloneDeep(this.dataSource);
      }
    }
  }
});
